import { CommonDialogs } from "@mcleod/common";
import { Button, ClickEvent, DataSourceMode, Layout, Panel, SaveButton, SlideoutDecorator, StepEvent } from "@mcleod/components";
import { Api, LogManager, Model, ModelRow, StringUtil } from "@mcleod/core";
import { CarrierAssignment, RowQuoteOrder } from "@mcleod/powerbroker";
import { AutogenLayoutCarrierSelectionStepper } from "./autogen/AutogenLayoutCarrierSelectionStepper";
import { CarrierSelection } from "./CarrierSelection";
import { ModelOrders, RowOrders } from "./models/ModelOrders";
import { UnassignCarrierPrompt } from "./UnassignCarrierPrompt";

const log = LogManager.getLogger("lme.dispatch.CarrierSelectionStepper");
const ORDER_INPROGRESS = "P";
const ORDER_DELIVERED = "D";

export interface CarrierSelectionProps {
    orderId: string,
    orderRow?: RowOrders | RowQuoteOrder,
    onClose?: (cancelled: boolean, updatedData?: ModelRow, hasSaved?: boolean) => void
}

export enum WorkflowStep {
    SELECTION = 0, ASSIGNMENT = 1
}

interface HeaderButton {
    id: string,
    caption: string,
    onClick: (event: ClickEvent) => void
}

const buttonProps = {
    backgroundColor: "primary.darker",
    color: "strokeSecondary",
    minWidth: 128,
    padding: 10,
    borderWidth: 1,
    rowBreak: false
};

export class CarrierSelectionStepper extends AutogenLayoutCarrierSelectionStepper {
    private onClose: (cancelled: boolean, updatedData?: ModelRow, hasSaved?: boolean) => void;
    private doAfterCarrierAssignmentSearch: (layout: CarrierAssignment) => void;
    private activeTrackingVendorsFound = false;
    private manualTrackingRequired = true;
    private _orderRow: RowOrders | RowQuoteOrder;
    private hasSaved = false;

    // Header Buttons
    private unassignBtn: Button;
    private saveAssignmentBtn: SaveButton;
    private saveSelectionBtn: SaveButton;

    public get orderRow(): RowOrders | RowQuoteOrder {
        return this._orderRow;
    }

    public set orderRow(value: RowOrders | RowQuoteOrder) {
        this._orderRow = value;
    }

    override onLoad() {
        this.stepperCarrierSelection.addBeforeStepListener((event: StepEvent) => {
            this.doBeforeStep(event);
        });
    }

    static showSlideout(props: CarrierSelectionProps) {
        const stepperLayout = Layout.getLayout("lme/dispatch/CarrierSelectionStepper", {
            width: window.innerWidth,
            backgroundColor: "defaultBackground",
            fillHeight: true,
            paddingBottom: 50
        }) as CarrierSelectionStepper;
        stepperLayout.onClose = props.onClose;

        const decorator = new SlideoutDecorator({
            layout: stepperLayout,
            title: `Carrier Selection - Order Number ${props.orderId}`,
            fillVerticalSpace: true,
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            doAfterSlideIn: async (decorator: SlideoutDecorator) => {
                stepperLayout.setupHeaderButtons(decorator, stepperLayout);
                await stepperLayout.prepareLayouts(props);
            },
            onClose: (cancelled: boolean) => decorator.slideOut().then(() => {
                if (props.onClose)
                    props.onClose(cancelled, stepperLayout.layoutAssigned.mainDataSource.activeRow, stepperLayout.hasSaved);
            })
        });
    }

    async prepareLayouts(props: CarrierSelectionProps) {
        await this.prepareCarrierSelectionLayout(props);
        this.prepareCarrierAssignmentLayout();
    }

    async prepareCarrierSelectionLayout(props: CarrierSelectionProps) {
        const carrierSelection = this.layoutSelection as CarrierSelection;
        this.orderRow = carrierSelection.orderRow = props?.orderRow ?? await new ModelOrders().searchSingle({ id: props.orderId }, "lme/dispatch/Orders");
        carrierSelection.doAfterChoosingCarrier = async () => {
            this.stepperCarrierSelection.selectedIndex = WorkflowStep.ASSIGNMENT;
            await this.setupCarrierAssignmentLayout(carrierSelection.chosenCarrier.get("rating_source"));
        }
        await carrierSelection.sourceBrltlBillingFreightGroup.search({ order_id: this.orderRow.get("id") });
    }

    async prepareCarrierAssignmentLayout() {
        this.layoutAssigned.panelHeader.visible = false;
        this.layoutAssigned.panelEquipment.visible = false;
        this.layoutAssigned.layoutMovementPay.panelRates.forEveryChildComponent(comp => {
            comp.enabled = false
        });
        this.layoutAssigned.layoutMovementPay.layoutCarrierRates.forEveryChildComponent(comp => comp.enabled = false);
        this.layoutAssigned.layoutMovementPay.tabPayAdjustment.forEveryChildComponent(comp => comp.enabled = false);
        this.layoutAssigned.panelReference.visible = true;
        this.layoutAssigned.panelContact.forEveryChildComponent(comp => comp.enabled = false);

        this.prepareSmc3Panels();
    }

    async prepareSmc3Panels() {
        const smc3Record = await Model.searchSingleRecord("lme/powerbroker/contract-management-control", { id: "SLA" });
        if (this.layoutSelection.chosenCarrier && smc3Record?.get("is_active") == "Y" &&
            this.layoutSelection.chosenCarrier.get("dispatch_to_smc3") == true) {

            this.layoutAssigned.panelSmc3Documents.visible = true;
            this.layoutAssigned.panelSmc3StatusHistory.visible = true;
            await Api.post("lme/powerbroker/smc3-initiate-tracking-manager", {
                bfg_uid: this.layoutSelection.chosenCarrier.get("bfg_uid")
            }).then((response) => {
                this.layoutAssigned.buttonRestartTracking.visible = response.data[0]?.["quote_allow_push_tracking"];
            });
            this.layoutAssigned.layoutRecentCallins.doSearch(this.orderRow.get("curr_movement_id"));
        }
    }

    setupHeaderButtons(decorator: SlideoutDecorator, stepperLayout: CarrierSelectionStepper) {
        // temporarily remove close button as it automatically gets added again w addHeaderActions
        const headerPanel = decorator.findComponentById("sodPanelHeader") as Panel;
        headerPanel.remove(decorator.findComponentById("sodCloseButton"));
        stepperLayout.generateHeaderButtons(decorator);
    }

    generateHeaderButtons(decorator: SlideoutDecorator) {
        this.unassignBtn = this.createHeaderButton({
            id: "buttonUnassign",
            caption: "Unassign",
            onClick: (event: ClickEvent) => this.unassignButtonOnClick(event)
        });
        this.saveAssignmentBtn = SaveButton.create({
            id: "buttonAssignment",
            caption: "Save",
            dataSource: this.layoutAssigned.mainDataSource,
            onClick: (event: ClickEvent) => this.doneButtonOnClick(event),
            minWidth: 128,
            marginLeft: 8,
            rowBreak: false,
            allowSaveAndClose: true
        });
        this.saveSelectionBtn = SaveButton.create({
            id: "buttonSaveSelection",
            caption: "Save",
            dataSource: this.layoutSelection.sourceBrltlBillingFreightGroup,
            onClick: (event: ClickEvent) => {
                // The active row of the data source was already posted by the SaveButton.save(),
                // so do not post again to avoid AUCRE
                this.layoutSelection.sourceBrltlBillingFreightGroup.data.forEach((row) => {
                    if (row != this.layoutSelection.sourceBrltlBillingFreightGroup.activeRow)
                        row.post()
                });
                this.layoutSelection.getRates();
            },
            minWidth: 128,
            rowBreak: false
        });
        decorator.addHeaderActions([this.unassignBtn, this.saveAssignmentBtn, this.saveSelectionBtn]);
        this.determineHeaderBtns(this.stepperCarrierSelection.selectedIndex);
    }

    determineHeaderBtns(index: number) {
        this.unassignBtn.visible = index === WorkflowStep.ASSIGNMENT && !["D", "P"].includes(this.orderRow.get("status"));
        this.saveAssignmentBtn.visible = index === WorkflowStep.ASSIGNMENT;
        this.saveSelectionBtn.visible = index === WorkflowStep.SELECTION;
    }

    private async doBeforeStep(event: StepEvent) {
        switch (event.newIndex) {
            case WorkflowStep.SELECTION:
                this.layoutSelection.buttonChangeMarkups.visible = !this.layoutSelection.chosenCarrier;
                break;
            case WorkflowStep.ASSIGNMENT:
                if (this.layoutSelection.chosenCarrier == null) {
                    event.preventDefault();
                    return;
                }
                break;
        }
        this.determineHeaderBtns(event.newIndex);
    }

    public setupCarrierAssignmentLayout(ratingSource?: string): Promise<CarrierAssignment> {
        return new Promise((resolve, reject) => {
            const layout = this.layoutAssigned as CarrierAssignment;
            layout.addLayoutLoadListener(() => {
                layout.sourceCarriersForAssignment.addDisplayListener((event) => {
                    if (event.rowData == null && layout.mainDataSource.activeRow != null) {
                        this.activeTrackingVendorsFound = layout.mainDataSource?.activeRow?.get("active_tracking_vendors_found", false);
                        if (this.activeTrackingVendorsFound) {
                            const manualTrackingRequiredOnMovement = layout.mainDataSource.activeRow?.getBoolean("manual_tracking_required", false);
                            this.setManualTrackingRequired(manualTrackingRequiredOnMovement);
                        } else {
                            this.setManualTrackingRequired(false);
                        }
                    } else if (!this.manualTrackingRequired && this.activeTrackingVendorsFound) {
                        const trackingDisabledOnPayee: boolean = layout.sourceCarriersForAssignment?.activeRow?.get("disable_automatic_tracking", "N") === "Y";
                        this.setManualTrackingRequired(trackingDisabledOnPayee);
                    }
                });
                layout.mainDataSource.search({
                    "id": this.orderRow.get("curr_movement_id"),
                    "orders.id": this.orderRow.get("id")
                }).then(async result => {
                    if (layout.mainDataSource?.activeRow == null) {
                        reject("Unable to find movement");
                    } else {
                        layout.setAllDataSourcesToMode(DataSourceMode.UPDATE);
                        const lockErrorMessage = layout.mainDataSource.activeRow.get("locked_error_message");
                        if (lockErrorMessage != null) {
                            reject(lockErrorMessage);
                        } else {
                            this.activeTrackingVendorsFound = layout.mainDataSource.activeRow?.get("active_tracking_vendors_found", false);
                            this.setManualTrackingRequired(layout.mainDataSource?.activeRow?.get("manual_tracking_required", false) == true);
                            layout.doAfterClose = (canceled: boolean) => this.close(canceled);
                            if (this.doAfterCarrierAssignmentSearch != null) {
                                this.doAfterCarrierAssignmentSearch(layout);
                            }
                            const carrierId = layout.mainDataSource.activeRow.get("override_payee_id");
                            if (carrierId != null) {
                                layout.textboxPendingPayeeId.field = "override_payee_id";
                                layout.activeRow.setLookupModelData("override_payee_id", new ModelRow(layout.textboxPendingPayeeId.lookupModel, false, {
                                    id: carrierId,
                                    name: layout.activeRow.get("payee.name")
                                }));
                                layout.textboxPendingPayeeId.displayData(layout.activeRow, null, 0);
                                layout.textboxPendingPayeeId.enabled = false;
                                layout.sourceCarriersForAssignment.search({ "id": carrierId });
                            }
                            const firstStopId = layout.mainDataSource.activeRow.get("origin_stop_id");
                            if (this.layoutSelection.chosenCarrier) {
                                const refId: string = this.layoutSelection.chosenCarrier.get("bfg_reference_id");
                                if (StringUtil.isEmptyString(refId) || !ratingSource.includes("SMC3")) {
                                    // This would be the case for an internal tariff or any non-SMC3 rated carrier.
                                    await layout.sourceReferenceNumber.search({ "stop_id": firstStopId, "reference_qual": "PU" });
                                } else {
                                    // For SMC3 rated carriers use the reference ID instead of searching for a PU reference number
                                    this.layoutAssigned.textboxReferenceNumber.text = refId;
                                }
                                if (ratingSource.includes("SMC3")) {
                                    const response = await this.layoutAssigned.sourceTerminalInfo.search({ "order_id": this.orderRow.get("id"),
                                        "ignore_ccxl_exceptions": true });
                                    const modelRowsReturned = response.modelRows.length > 0;
                                    const errorReason = modelRowsReturned ? response.modelRows[0].data["error_reason"] : null;
                                    if (modelRowsReturned && errorReason == null) {
                                        this.layoutAssigned.panelTerminalInfo.visible = true;
                                    }
                                }
                            }
                            layout.layoutMovementPay.calculatePay(false, undefined, undefined, undefined, false);
                            resolve(layout);
                        }
                    }
                });
                this.prepareSmc3Panels();
            });
        });
    }

    private setManualTrackingRequired(value: boolean) {
        if (this.manualTrackingRequired != value) {
            this.manualTrackingRequired = value;
        }
    }

    close(cancelled: boolean, updatedData?: ModelRow, hasSaved?: boolean) {
        this.parent.parent.slideOut().then(() => {
            if (this.onClose) this.onClose(cancelled, updatedData, hasSaved);
        });
    }

    createHeaderButton(props: HeaderButton) {
        return new Button({
            id: props.id, caption: props.caption, ...buttonProps, onClick: props.onClick
        });
    }

    async doneButtonOnClick(event: ClickEvent) {
        const doneButton = event.target as Button;
        doneButton.busy = true;
        this.hasSaved = true;
        if (this.layoutAssigned.sourceReferenceNumber.activeRow != null) {
            await this.layoutAssigned.sourceReferenceNumber.execute().then(() => {
                this.close(false, this.layoutAssigned.mainDataSource.activeRow, this.hasSaved);
            });
        } else
            this.close(false, this.layoutAssigned.mainDataSource.activeRow, this.hasSaved);
        doneButton.busy = false
    }

    async unassignButtonOnClick(event: ClickEvent) {
        const button = event.target as Button;
        button.busy = true;
        const canCancel = await this.orderHasValidStatusForCancelDispatch(this.orderRow.get("status"));

        if (!canCancel) {
            button.busy = false;
            return;
        }

        let cancelSupported = true;
        const chosenCarrier: ModelRow = this.layoutSelection.chosenCarrier;
        const carrierDispatchType = chosenCarrier?.get("dispatch_type", null);
        const carrierName = chosenCarrier.get("carrier_name");
        const { isP44Active, isSmc3Active } = await this.getP44AndSmc3ActiveStatus();
        if (isP44Active && carrierDispatchType === "P44") {
            cancelSupported = await this.isP44SupportsDispatchCancellation(chosenCarrier);
        } else if(isSmc3Active && carrierDispatchType === "SMC3") {
            cancelSupported = await this.isSmc3SupportsDispatchCancellation(chosenCarrier);
        }

        if (!cancelSupported) {
            const integrationType = carrierDispatchType === "P44" ? "Project44" : "SMC3";
            const message =
                `Dispatch cancellation is not supported for this ${integrationType} carrier (${carrierName}). ` +
                `You will need to contact the carrier directly to cancel the dispatch. ` +
                `\nDo you want to unassign this carrier?`;
            cancelSupported = await CommonDialogs.showYesNo( message, "Unassign Carrier");
        }

        if (cancelSupported) {
            const moveRow = this.layoutSelection.mainDataSource.activeRow;
            await this.removeCarrier(moveRow, async (success: boolean, vendorName: string, forceCancel: boolean, unassignLayout: UnassignCarrierPrompt) => {
                unassignLayout.toastMessage = `${carrierName} was removed from movement ${moveRow.getString("movement_id", "")}.`;
                this.layoutSelection.chosenCarrier = null;
                await this.layoutSelection.mainDataSource.search({ order_id: this.orderRow.get("id") });
                this.stepperCarrierSelection.selectedIndex = WorkflowStep.SELECTION;
            });
        }
        button.busy = false;
    }

    private async isSmc3SupportsDispatchCancellation(chosenCarrier: ModelRow<any>) {
        const result = await Api.post("lme/powerbroker/smc3-dispatch-cancel", { bfg_uid: chosenCarrier.get("bfg_uid") });
        const data = result?.data[0];
        return data?.carrier_cancel_support;
    }

    private async isP44SupportsDispatchCancellation(chosenCarrier: ModelRow<any>) {
        const result = await Api.post("lme/powerbroker/get-p44-carrier-cancel-support", { bfg_uid: chosenCarrier.get("bfg_uid") });
        const data = result?.data[0];
        return data?.carrier_cancel_support;
    }

    async getP44AndSmc3ActiveStatus() {
        const response = await Api.search("lme/dispatch/contract-management-records");
        const data = response.data[0];
        return {
            isP44Active: data?.p44_active,
            isSmc3Active: data?.smc3_active,
        };
    }

    async removeCarrier(moveRow: ModelRow<any>, doAfterSave?: (success: boolean, vendorName: string, forceCancel: boolean, unassignLayout: UnassignCarrierPrompt) => Promise<void>) {
        UnassignCarrierPrompt.showPrompt(moveRow, null, doAfterSave);
    }

    async orderHasValidStatusForCancelDispatch(movementStatus: string): Promise<boolean> {
        const title = "Unassign Carrier";
        if (movementStatus === ORDER_INPROGRESS) {
            return CommonDialogs.showYesNo("This order is already in progress. Do you want to cancel the dispatch?", title);
        }
        if (movementStatus === ORDER_DELIVERED) {
            return CommonDialogs.showYesNo("This order is already delivered. Do you want to cancel the dispatch?", title);
        }
        return true;
    }
}
